import { DOCUMENT } from '@angular/common';
import type { AfterViewInit, OnDestroy } from '@angular/core';
import {
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { isDefined } from '@freelancer/utils';
import { Autolinker } from 'autolinker';
import type { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Detect `#` or `@` tags and render those as tags.
 */
export enum TagType {
  AT = '@',
  HASH_TAG = '#',
}

export interface Tag {
  // The identified tag
  content: string;
  // Tag offset in the text
  offset: number;
  // Type of the tag
  type: TagType;
}

export interface TagResult {
  current?: Tag;
  tags: readonly Tag[];
}

@Directive({ selector: '[flTagging]' })
export class TaggingDirective implements AfterViewInit, OnDestroy {
  @Input() flTagging: FormControl<string>;
  @Input() flTaggingTypes: readonly TagType[] = [TagType.AT, TagType.HASH_TAG];
  @Input() flTaggingLimit = 100;
  @Output() flTaggingResult = new EventEmitter<TagResult>();

  private autolinker = new Autolinker({
    urls: false,
    email: false,
    phone: false,
    mention: this.flTaggingTypes.includes(TagType.AT) ? 'twitter' : false,
    hashtag: this.flTaggingTypes.includes(TagType.HASH_TAG)
      ? 'facebook'
      : false,
  });

  private formValueChangesSubscription?: Subscription;
  private tags: readonly Tag[] = [];

  private acceptedNode: HTMLInputElement | HTMLTextAreaElement;

  constructor(
    private _element: ElementRef,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngAfterViewInit(): void {
    const acceptedNode = this.getAcceptedNode(this._element.nativeElement);
    if (!acceptedNode) {
      throw new Error('Unsupported element');
    }
    this.acceptedNode = acceptedNode;
    this.formValueChangesSubscription = this.flTagging.valueChanges
      .pipe(filter(isDefined))
      .subscribe(content => this.collectTags(content));
  }

  ngOnDestroy(): void {
    if (this.formValueChangesSubscription) {
      this.formValueChangesSubscription.unsubscribe();
    }
  }

  private collectTags(text: string): void {
    const matches = this.autolinker.parse(text);
    this.tags = matches.map(match => ({
      content: match.getMatchedText().substring(1),
      offset: match.getOffset(),
      type: this.getTagType(match.getType()),
    }));

    const caretPosition = this.getCaretPosition();
    if (caretPosition) {
      this.collectEmptyTag(text, caretPosition);
    }

    this.flTaggingResult.emit({
      current: caretPosition ? this.getTagByPosition(caretPosition) : undefined,
      tags: this.tags,
    });
  }

  private getTagType(type: string): TagType {
    switch (type) {
      case 'mention':
        return TagType.AT;

      case 'hashtag':
        return TagType.HASH_TAG;

      default:
        throw new Error(`Unexpected tagging symbol: ${type}`);
    }
  }

  private collectEmptyTag(text: string, caretPosition: number): void {
    let emptyTag: TagType | null = null;
    const tagType = text.substring(caretPosition - 1, caretPosition);
    if (tagType === TagType.AT && this.flTaggingTypes.includes(TagType.AT)) {
      emptyTag = TagType.AT;
    } else if (
      tagType === TagType.HASH_TAG &&
      this.flTaggingTypes.includes(TagType.HASH_TAG)
    ) {
      emptyTag = TagType.HASH_TAG;
    }

    if (emptyTag !== null) {
      this.tags = [
        ...this.tags,
        {
          content: '',
          offset: caretPosition - 1,
          type: emptyTag,
        },
      ];
    }
  }

  private getTagByPosition(offset: number): Tag | undefined {
    const result = this.tags.find(
      tag =>
        tag.offset < offset && tag.offset + tag.content.length + 1 >= offset,
    );
    if (!result) {
      return undefined;
    }
    this.tags = [...this.tags.filter(tag => tag !== result)];
    return result;
  }

  /**
   * Calculates the caret position in the acceptedNode.
   * This position is the offset in the text.
   *
   * @return number|null
   */
  private getCaretPosition(): number | null {
    // for texterea/input element
    return this.acceptedNode.selectionStart;
  }

  private getAcceptedNode(
    element: Node,
  ): HTMLInputElement | HTMLTextAreaElement | undefined {
    const nodeIterator = this.document.createNodeIterator(
      element,
      NodeFilter.SHOW_ELEMENT,
      null,
    );

    let node: HTMLInputElement | HTMLTextAreaElement | undefined;
    while (nodeIterator.nextNode()) {
      const currentNode = nodeIterator.referenceNode;
      if (this.isAcceptedNode(currentNode)) {
        node = currentNode;
        break;
      }
    }

    return node;
  }

  private isAcceptedNode(
    currentNode: Node,
  ): currentNode is HTMLInputElement | HTMLTextAreaElement {
    return (
      currentNode instanceof HTMLInputElement ||
      currentNode instanceof HTMLTextAreaElement
    );
  }
}
